import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	DeviceCategory,
	isDeviceGateway,
	isDevicePaired,
	VelavuAPI,
	VelavuDevice,
	VelavuFloor,
	VelavuSite,
} from "velavu-js-api";
import IconDelete from "../../../../dynamicicons/icon-delete";
import IconEdit from "../../../../dynamicicons/icon-edit";
import Divider from "../../../../elements/divider";
import DropdownPanelToolbar from "../../../../elements/dropdown-panel-toolbar";
import VelavuMenu, {
	VelavuMenuItem,
	VelavuOverflowButton,
} from "../../../../elements/velavu-menu";
import { useVelavuModal } from "../../../../elements/velavu-modal";
import VelavuAlertModal from "../../../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuButton from "../../../../elements/velavu-button";
import VelavuTabSwitcher from "../../../../elements/velavu-tab-switcher";
import { useGeocoder } from "../../../../helper/api-helper";
import useAssetFiltering from "../../../../helper/assets-filter-hook";
import AssetListFilter from "../asset-list/asset-list-filter";
import AssetListItem from "../asset-list/asset-listitem";
import PinnedList from "../pinned-list";
import DevicesToolbar from "./devices-toolbar/devices-toolbar";
import SiteDetailAnchors from "./site-detail-anchors/site-detail-anchors";
import SiteDetailFloors from "./site-detail-floors/site-detail-floors";
import SiteDetailGateways from "./site-detail-gateways/site-detail-gateways";
import SiteDetailStatus from "./site-detail-status";
import * as styles from "./site-detail.module.scss";
import { SiteDetailState } from "./site-detail-state";
import SiteDetailEvents from "./site-detail-events";
import FloorFilter from "../../../../data/floor-filter";
import {
	PendingFloorDuplication,
	requestDuplicateFloor,
} from "../../../../state/floor/floor-duplicate";
import VelavuAlertModalObject from "../../../../elements/velavu-alert-modal/velavu-alert-modal-object";
import { useHasPermission } from "../../../sign-in-context/sign-in-context";

export enum SiteDetailsTab {
	Status,
	Floors,
	Gateways,
	Anchors,
	Events,
}

export interface SiteDetailProps {
	site: VelavuSite;
	siteDetailState: SiteDetailState;

	onBack?: VoidFunction;
	onClose?: VoidFunction;
	onEdit?: VoidFunction;
	onDelete?: VoidFunction;

	onDeletePlan?: (floor: VelavuFloor) => void;
	onEditFloor?: (floor: VelavuFloor) => void;
	onCreateFloor?: () => void;
	onAddFloor?: (floor: VelavuFloor, siteID: string) => void;

	selectedFloor: string | undefined;
	onSelectFloor?: (id: string | undefined) => void;

	showFloorFilter?: boolean;
	floorFilter: FloorFilter;
	onSetFloorFilter: (floorFilter: FloorFilter) => void;

	onHoverDevice?: (deviceID: string | undefined) => void;
	onSelectDevice?: (deviceID: string) => void;

	onPinNewGateway?: (gateway: VelavuDevice) => void;
	onPinNewAnchor?: (anchor: VelavuDevice) => void;
}

export default function SiteDetail(props: SiteDetailProps) {
	const { t } = useTranslation();

	const canWriteSites = useHasPermission("sites:write");
	const canDeleteSites = useHasPermission("sites:delete");
	const showSiteMenu = canWriteSites || canDeleteSites;

	const { devices, selectedTab, setSelectedTab, showAssets, setShowAssets } =
		props.siteDetailState;

	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) =>
			setMenuAnchor(event.currentTarget),
		[setMenuAnchor],
	);
	const closeMenu = useCallback(() => setMenuAnchor(null), [setMenuAnchor]);
	const address = useGeocoder(props.site.coordinates);

	const { pushModal } = useVelavuModal();
	const propsSite = props.site;
	const propsOnEdit = props.onEdit;
	const editSite = useCallback(() => {
		closeMenu();
		if (propsOnEdit !== undefined) propsOnEdit();
	}, [closeMenu, propsOnEdit]);
	const propsOnDelete = props.onDelete;
	const deleteSite = useCallback(() => {
		closeMenu();
		pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("dashboard.site_detail.delete.title")}
				</VelavuAlertModalTitle>

				<VelavuAlertModalObject>
					{propsSite.name}
				</VelavuAlertModalObject>

				<VelavuAlertModalContent>
					{t("dashboard.site_detail.delete.body")}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("common.delete")}
						variant="danger"
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		)).then((result) => {
			if (result) {
				VelavuAPI.sites.deleteExistingSite(propsSite.id).catch(() =>
					alert(
						t("dashboard.site_detail.delete.failed", {
							id: propsSite.name,
						}),
					),
				);
				if (propsOnDelete !== undefined) propsOnDelete();
			}
		});
	}, [closeMenu, pushModal, propsSite, propsOnDelete, t]);

	const anchors = useMemo(
		() =>
			devices?.filter(
				(device) => device.category === DeviceCategory.Anchor,
			),
		[devices],
	);

	const onlineAnchors = useMemo(
		() => anchors?.filter((anchor) => anchor.online),
		[anchors],
	);

	const assets = useMemo(() => {
		return devices
			.map((device) => device.asset)
			.filter((it) => it !== undefined);
	}, [devices]);

	const pairedDevices = useMemo(() => {
		return devices.filter((device) => isDevicePaired(device));
	}, [devices]);

	const onlineAssets = useMemo(
		() => assets.filter((asset) => asset.online),
		[assets],
	);

	const gateways = useMemo(
		() => devices.filter((device) => isDeviceGateway(device)),
		[devices],
	);

	const onlineGateways = useMemo(
		() => gateways?.filter((gateway) => gateway.online),
		[gateways],
	);

	const propsOnAddFloor = props.onAddFloor;
	const [floorDuplications, setFloorDuplications] = useState<
		PendingFloorDuplication[]
	>([]);
	const duplicateFloor = useCallback(
		async (floor: VelavuFloor) => {
			//Create the request
			const request = requestDuplicateFloor(props.site.id, floor);

			//Add the pending duplication to the list
			setFloorDuplications((array) => {
				return [...array, request.pending];
			});

			//Wait for the floor to be updated
			let updatedFloor: VelavuFloor;
			try {
				updatedFloor = await request.promise;
			} catch (error) {
				alert(
					t("dashboard.site_detail.duplicate_floor_failed", {
						id: floor.name,
					}),
				);
				return;
			} finally {
				//Remove the pending duplication from the list
				setFloorDuplications((array) => {
					return array.filter(
						(item) => item.id !== request.pending.id,
					);
				});
			}

			//Notify the parent
			propsOnAddFloor?.(updatedFloor, props.site.id);
		},
		[props.site, propsOnAddFloor, t],
	);

	const {
		devices: filteredDevices,
		filterCategory,
		filterStatus,
		isFiltering,
		searchText,
		setFilterCategory,
		setFilterStatus,
		setSearchText,
		showFilterMenu,
		toggleFilterMenu,
	} = useAssetFiltering(
		pairedDevices,
		props.floorFilter === FloorFilter.Current
			? props.selectedFloor
			: undefined,
	);

	const filterTargetFloor = useMemo(() => {
		if (
			props.floorFilter === FloorFilter.All ||
			props.selectedFloor === undefined ||
			props.site.floors === undefined
		) {
			return undefined;
		}

		return props.site.floors.find(
			(floor) => floor.id === props.selectedFloor,
		);
	}, [props.floorFilter, props.selectedFloor, props.site.floors]);

	return (
		<>
			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
			>
				<VelavuMenuItem
					icon={(size, color) => (
						<IconEdit size={size} color={color} />
					)}
					label={t("common.edit_site")}
					onClick={editSite}
				/>
				<VelavuMenuItem
					icon={(size, color) => (
						<IconDelete size={size} color={color} />
					)}
					label={t("common.delete_site")}
					onClick={deleteSite}
					danger
				/>
			</VelavuMenu>

			<PinnedList>
				<DropdownPanelToolbar
					onBack={
						showAssets ? () => setShowAssets(false) : props.onBack
					}
					onClose={props.onClose}
				/>

				{showAssets ? (
					<>
						<div className={styles.assetCountCard}>
							{assets === undefined ? (
								<span>{t("common.loading_devices")}</span>
							) : (
								<React.Fragment>
									<span
										className={styles.assetCountCardNumber}
									>
										{assets.length}
									</span>

									<span
										className={styles.assetCountCardLabel}
									>
										{t(
											"dashboard.site_detail.assets_in_site",
										)}
									</span>
								</React.Fragment>
							)}
						</div>

						<DevicesToolbar
							searchText={searchText}
							setSearchText={setSearchText}
							levelSelection={filterTargetFloor?.level}
							onChangeFloor={props.onSetFloorFilter}
							showFloorFilter={props.showFloorFilter}
							highlightFilter={isFiltering}
							showFilterMenu={showFilterMenu}
							onClickShowFilterMenu={toggleFilterMenu}
						/>

						<Divider />

						{showFilterMenu && (
							<>
								<AssetListFilter
									filterCategory={filterCategory}
									selectCategory={setFilterCategory}
									filterStatus={filterStatus}
									selectStatus={setFilterStatus}
								/>
								<Divider />
							</>
						)}

						<div className={styles.list}>
							{filteredDevices.map((device) => (
								<AssetListItem
									key={device.asset.id}
									asset={device.asset}
									onClick={() =>
										props.onSelectDevice?.(device.id)
									}
									onMouseEnter={() =>
										props.onHoverDevice?.(device.id)
									}
									onMouseLeave={() =>
										props.onHoverDevice?.(undefined)
									}
								/>
							))}
						</div>
					</>
				) : (
					<>
						<div className={styles.titleHeader}>
							<div className={styles.titleText}>
								<span className={styles.titleHeaderTitle}>
									{props.site.name}
								</span>
								<span className={styles.titleHeaderSubtitle}>
									{address}
								</span>
							</div>

							{showSiteMenu && (
								<VelavuOverflowButton
									onClick={openMenu}
									open={!!menuAnchor}
								/>
							)}
						</div>

						<VelavuTabSwitcher
							selectedKey={selectedTab}
							onSelectKey={setSelectedTab}
							labels={{
								[SiteDetailsTab.Status]: t(
									"dashboard.tabs.status",
								),
								[SiteDetailsTab.Floors]: t(
									"dashboard.tabs.floors",
								),
								[SiteDetailsTab.Gateways]: t(
									"dashboard.tabs.gateways",
								),
								[SiteDetailsTab.Anchors]: t(
									"dashboard.tabs.anchors",
								),
								[SiteDetailsTab.Events]: t(
									"dashboard.tabs.events",
								),
							}}
							compact
						>
							{selectedTab === SiteDetailsTab.Status && (
								<SiteDetailStatus
									anchorCountOnline={onlineAnchors?.length}
									anchorCountTotal={anchors?.length}
									assetCountOnline={onlineAssets?.length}
									assetCountTotal={assets?.length}
									gatewayCountOnline={onlineGateways?.length}
									gatewayCountTotal={gateways?.length}
									gateways={gateways}
									online={props.site.online}
									onSelectGateways={() => setSelectedTab(2)}
									onSelectAnchors={() => {
										setSelectedTab(3);
									}}
									onSelectAssets={() => setShowAssets(true)}
								/>
							)}

							{selectedTab === SiteDetailsTab.Floors && (
								<SiteDetailFloors
									site={props.site}
									onDeletePlan={props.onDeletePlan}
									selectedFloor={props.selectedFloor}
									onSelectFloor={props.onSelectFloor}
									onEditFloor={props.onEditFloor}
									onCreateFloor={props.onCreateFloor}
									onDuplicateFloor={duplicateFloor}
									floorDuplications={floorDuplications}
								/>
							)}

							{selectedTab === SiteDetailsTab.Gateways && (
								<SiteDetailGateways
									siteID={props.site.id}
									gateways={gateways}
									onSelectGateway={props.onSelectDevice}
									onHoverGateway={props.onHoverDevice}
									filterTargetFloor={filterTargetFloor}
									onChangeFloorFilter={props.onSetFloorFilter}
									onPinNewGateway={props.onPinNewGateway}
									showFloorFilter={props.showFloorFilter}
								/>
							)}

							{selectedTab === SiteDetailsTab.Anchors && (
								<SiteDetailAnchors
									siteID={props.site.id}
									anchors={anchors}
									onSelectAnchor={props.onSelectDevice}
									onHoverAnchor={props.onHoverDevice}
									filterTargetFloor={filterTargetFloor}
									onChangeFloorFilter={props.onSetFloorFilter}
									onPinNewAnchor={props.onPinNewAnchor}
									showFloorFilter={props.showFloorFilter}
								/>
							)}

							{selectedTab === SiteDetailsTab.Events && (
								<SiteDetailEvents siteID={props.site.id} />
							)}
						</VelavuTabSwitcher>
					</>
				)}
			</PinnedList>
		</>
	);
}
